import { z } from "zod";
import { PackIdSchema } from "./packs";

/** The plan through Stripe (M7 round four; brief `docs/briefs/m7-billing.md`, task 3 and task 5, draft 2's B3, B8, S9): the pure rules
 *  and the answers of the owner's billing routes. Stripe's notices change the plan row through the server; these are the words the
 *  product uses for what Stripe says, and the one rule that turns Stripe's price into a catalogue version. */

/** our four words for Stripe's eight subscription statuses */
export const SUBSCRIPTION_STATUSES = ["none", "active", "past_due", "canceled"] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];
/** a live subscription: Stripe bills the workspace (and keeps its period while it retries a failed payment) */
export const LIVE_SUBSCRIPTION_STATUSES: readonly SubscriptionStatus[] = ["active", "past_due"];
export const isLiveSubscription = (status: string): boolean => (LIVE_SUBSCRIPTION_STATUSES as readonly string[]).includes(status);

/** Stripe's status in our words: a trial counts as active (we sell none, S7); unpaid is still past due; an incomplete first payment is
 *  none (no money ever arrived, the workspace never moved) */
export function subscriptionStatusOf(stripeStatus: string): SubscriptionStatus {
  switch (stripeStatus) {
    case "active": case "trialing": return "active";
    case "past_due": case "unpaid": return "past_due";
    case "canceled": return "canceled";
    default: return "none";
  }
}

export const SubscriptionReadSchema = z.object({ status: z.enum(SUBSCRIPTION_STATUSES), cancelAtPeriodEnd: z.boolean(), periodEnd: z.string().nullable() });
export type SubscriptionRead = z.infer<typeof SubscriptionReadSchema>;

type CatalogueRow = { id: string; validFrom: string; retiredAt: string | null; stripePriceMonth: string | null };
type Pointer = { id: string; validFrom: string };
/** From Stripe's price to the catalogue, through the workspace's pointer (S9): the price names a plan id (any version that carries it);
 *  the same id as the pointer keeps the pointer as it is (a renewal never moves a grandfathered workspace to a newer version); another id
 *  is that id's live version (the newest without `retiredAt`); a price no row carries is null (ignored, never guessed). */
export function catalogueVersionFor(current: Pointer | null, priceId: string, rows: readonly CatalogueRow[]): Pointer | null {
  const planId = rows.find((r) => r.stripePriceMonth !== null && r.stripePriceMonth === priceId)?.id;
  if (!planId) return null;
  if (current && current.id === planId) return current;
  const live = rows.filter((r) => r.id === planId && r.retiredAt === null).sort((a, b) => new Date(b.validFrom).getTime() - new Date(a.validFrom).getTime())[0];
  return live ? { id: live.id, validFrom: live.validFrom } : null;
}

/** the plans Stripe sells (Free and Beta are never bought; yearly prices never travel in this body) */
export const PAID_PLAN_IDS = ["starter", "team", "business"] as const;
/** the one checkout address buys a plan or (M7 round five) a credit pack, never both at once */
const PlanCheckoutSchema = z.object({ planId: z.enum(PAID_PLAN_IDS) }).strict();
export const PackCheckoutSchema = z.object({ packId: PackIdSchema }).strict();
export const BillingCheckoutSchema = z.union([PlanCheckoutSchema, PackCheckoutSchema]);
/** a plan's checkout (the body's plan half) */
export type BillingCheckout = z.infer<typeof PlanCheckoutSchema>;
export type PackCheckout = z.infer<typeof PackCheckoutSchema>;
export type BillingCheckoutBody = z.infer<typeof BillingCheckoutSchema>;
/** checkout and the Portal answer one thing: the address of Stripe's page */
export const BillingUrlSchema = z.object({ url: z.string().url() });
export type BillingUrl = z.infer<typeof BillingUrlSchema>;
/** Stripe's invoices of the workspace's customer, newest first; drafts are never listed */
export const InvoiceRowSchema = z.object({
  id: z.string(), number: z.string().nullable(), createdAt: z.string(), amountCents: z.number().int(), currency: z.string(),
  status: z.enum(["open", "paid", "void", "uncollectible"]), hostedUrl: z.string().nullable(), pdfUrl: z.string().nullable(),
});
export const InvoicesReadSchema = z.object({ rows: z.array(InvoiceRowSchema) });
export type InvoiceRow = z.infer<typeof InvoiceRowSchema>;
export type InvoicesRead = z.infer<typeof InvoicesReadSchema>;
/** Payment details' company lines, read from Stripe's customer (what Checkout collected); null before the first purchase */
export const BillingCustomerReadSchema = z.object({ customer: z.object({ name: z.string().nullable(), email: z.string().nullable(), country: z.string().nullable(), vatId: z.string().nullable() }).nullable() });
export type BillingCustomerRead = z.infer<typeof BillingCustomerReadSchema>;
